import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { organizationService } from "../../../services/api/organization";
import { toast } from "sonner";
import Button2 from "../../../components/elements/forms/Button2";
import SlaContent from "./SlaContent";
import StatusModal from "../../../components/Modal/StatusModal";
import { format } from "date-fns";
import MSAContent from "../master-service-agreement/MSAContent";
import DPAContent from "../data-processing/DPAContent";

const SlaPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const id = searchParams.get("Id");

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientName, setClientName] = useState("[Client]");
  const [representativeName, setRepresentativeName] = useState(
    "[Representative Name]",
  );
  const [representativeRole, setRepresentativeRole] = useState(
    "[Representative Role]",
  );
  const [agreed, setAgreed] = useState(false);
  const [statusModal, setStatusModal] = useState<{
    isOpen: boolean;
    status: "success" | "error";
    message: string;
  }>({
    isOpen: false,
    status: "success",
    message: "",
  });

  const steps = [
    { title: "MSA", component: MSAContent },
    { title: "SLA", component: SlaContent },
    { title: "DPA", component: DPAContent },
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    const fetchOrg = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const response = await organizationService.get(id);
        if (response?.data?.name) {
          setClientName(response.data.name);
        }
        if (response?.data?.representativeName) {
          setRepresentativeName(response.data.representativeName);
        }
        if (response?.data?.representativeRole) {
          setRepresentativeRole(response.data.representativeRole);
        }
      } catch (error) {
        console.error("Failed to fetch organization details", error);
        toast.error("Could not load organization details.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrg();
  }, [id]);

  const handleAgree = async () => {
    if (!agreed) {
      toast.error("Please agree to the terms to continue.");
      return;
    }

    if (!id) {
      toast.error("Organization ID is missing.");
      return;
    }

    setIsSubmitting(true);
    try {
      const tier = searchParams.get("tier");
      const frequency = searchParams.get("frequency");
      const featureIds =
        searchParams.get("featureIds")?.split(",").filter(Boolean) || [];
      const directPlan = searchParams.get("directPlan");
      const includeSetupFee = searchParams.get("includeSetupFee") === "true";
      const directFeatureIds =
        searchParams.get("directAddOnIds")?.split(",").filter(Boolean) || [];
      const lifecyclePhaseIds =
        searchParams.get("lifecyclePhaseIds")?.split(",").filter(Boolean) || [];
      const amount = searchParams.get("amount")
        ? Number(searchParams.get("amount"))
        : undefined;

      const payload: any = {
        tier: tier || undefined,
        organizationId: id,
        frequency: frequency || undefined,
        featureIds,
        amount,
        lifecyclePhaseIds,
      };

      if (directPlan) {
        payload.directPlan = {
          tier: directPlan,
          includeSetupFee,
        };
      }

      if (directFeatureIds.length > 0) {
        payload.directFeatureIds = directFeatureIds;
      }

      const response = await organizationService.sendSlaEmail(id, payload);
      setStatusModal({
        isOpen: true,
        status: "success",
        message: response.message || "SLA Email sent!",
      });
    } catch (error: any) {
      console.error("Failed to send SLA email", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send SLA email";
      setStatusModal({
        isOpen: true,
        status: "error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  const CurrentComponent = steps[currentStep].component;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-[1024px] mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
            Review Documents
          </h1>

          {/* Stepper */}
          <div className="flex items-center justify-center mb-8">
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                <div className="flex flex-col items-center relative">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                      currentStep >= index
                        ? "bg-primary text-white scale-110 shadow-md"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {index + 1}
                  </div>
                  <span
                    className={`absolute -bottom-6 whitespace-nowrap text-xs font-medium transition-colors duration-300 ${
                      currentStep === index ? "text-primary" : "text-gray-400"
                    }`}
                  >
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`w-20 h-0.5 mx-2 transition-colors duration-300 ${
                      currentStep > index ? "bg-primary" : "bg-gray-200"
                    }`}
                  ></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2 md:p-6 mb-8 min-h-[600px]">
          {/* <h2 className="text-xl font-bold mb-6 text-gray-700 border-b pb-4">
            {steps[currentStep].title}
          </h2> */}

          {currentStep === 0 && (
            <MSAContent
              clientName={clientName}
              representativeName={representativeName}
              representativeRole={representativeRole}
              date={format(new Date(), "do MMMM, yyyy")}
              effectiveDate={format(new Date(), "do MMMM, yyyy")}
              readOnly={true}
            />
          )}
          {currentStep === 1 && (
            <SlaContent
              clientName={clientName}
              representativeName={representativeName}
              representativeRole={representativeRole}
              date={format(new Date(), "do MMMM, yyyy")}
            />
          )}
          {currentStep === 2 && (
            <DPAContent
              clientName={clientName}
              representativeName={representativeName}
              representativeRole={representativeRole}
              date={format(new Date(), "do MMMM, yyyy")}
              effectiveDate={format(new Date(), "do MMMM, yyyy")}
              readOnly={true}
            />
          )}
        </div>

        {/* Navigation Footer */}
        <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 sticky bottom-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {currentStep === steps.length - 1 && (
              <>
                <input
                  type="checkbox"
                  id="agree-checkbox"
                  className="w-5 h-5 cursor-pointer accent-primary shrink-0"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <label
                  htmlFor="agree-checkbox"
                  className="text-gray-700 cursor-pointer select-none text-sm leading-tight"
                >
                  I agree to the complete Enterprise Service Agreement Pack{" "}
                  {clientName !== "[Client]" ? `for ${clientName}` : ""}
                </label>
              </>
            )}
          </div>

          <div className="flex gap-3 w-full md:w-auto">
            <button
              onClick={prevStep}
              className={`flex-1 md:flex-none px-8 py-2.5 border border-gray-200 rounded-lg font-medium transition-all ${
                currentStep === 0
                  ? "opacity-0 pointer-events-none"
                  : "hover:bg-gray-50 text-gray-700 active:scale-95"
              }`}
            >
              Previous
            </button>

            {currentStep < steps.length - 1 ? (
              <button
                onClick={nextStep}
                className="flex-1 md:flex-none px-10 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all active:scale-95 shadow-lg shadow-primary/20"
              >
                Next
              </button>
            ) : (
              <Button2
                title="Agree & Submit"
                loading={isSubmitting}
                onSubmitHandler={handleAgree}
                className={`flex-1 md:flex-none px-10 py-2.5 rounded-lg text-white font-medium transition-all active:scale-95 shadow-lg ${
                  agreed && !isSubmitting
                    ? "bg-primary hover:bg-primary/90 shadow-primary/20"
                    : "bg-gray-300 cursor-not-allowed shadow-none"
                }`}
                disabled={!agreed || isSubmitting}
              />
            )}
          </div>
        </div>
      </div>

      <StatusModal
        isOpen={statusModal.isOpen}
        status={statusModal.status}
        message={statusModal.message}
        onClose={() => setStatusModal((prev) => ({ ...prev, isOpen: false }))}
        onAction={
          statusModal.status === "success"
            ? () =>
                navigate({
                  pathname: "/login",
                  search: searchParams.toString(),
                })
            : undefined
        }
      />
    </div>
  );
};

export default SlaPage;
